import React, { useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { PUBLIC_GET_CIAM_STATUS_BY_EMAIL } from 'Queries/Queries';
import { useNESteps } from 'context/NEStepsContext';
import { useFormik } from 'formik';

import { PUBLIC_NO_AUTH_CONFIG } from 'components/ApolloComponent/Apollo';
import { getCiamEmailErrorContent } from 'components/CiamEmail/CiamEmailErrors';
import { isSubAccountEnabled } from 'components/DebFormComponents/Table/utils';
import { ErrorBanner } from 'components/ErrorBanner/ErrorBanner';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { FormSection } from 'components/FormHelper/FormSection';
import { InputField } from 'components/FormHelper/InputField';
import {
  NE_CURRENT_TEST_SUITE,
  shouldFillNETestData,
} from 'components/NEForm/NETestdata/NETestSetup';
import { YNEEmailSchema } from 'components/NEValidation/NEEmailValidation';

import { NE_ACTIVE_STEP } from 'global/constants';

import { FFormDebug } from 'utils/FFormDebug';
import { checkCiamEmailByQuery } from 'utils/ciamEmailValidation.utils';

import './NEStepEmailInput.sass';

export const NEStepEmailInput = () => {
  const { activeStep, setActiveStep, setSubmittedValuesWithStep } = useNESteps();
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const [getCiamStatusByEmail] = useLazyQuery(
    PUBLIC_GET_CIAM_STATUS_BY_EMAIL,
    PUBLIC_NO_AUTH_CONFIG
  );

  const formik = useFormik({
    initialValues: {
      email: shouldFillNETestData ? NE_CURRENT_TEST_SUITE.step0.email : '',
    },
    validationSchema: YNEEmailSchema,
    validateOnMount: true,
    enableReinitialize: true,
    validateOnChange: true,
  });

  const checkEmail = async () => {
    if (formik.values.email && Object.keys(formik.errors).length === 0) {
      setIsLoading(true);
      setErrorMessage('');

      const result = await checkCiamEmailByQuery(getCiamStatusByEmail, formik.values.email);
      setIsLoading(false);

      if (result.isValid) {
        setSubmittedValuesWithStep(NE_ACTIVE_STEP.EMAIL_INPUT, formik.values);
        setActiveStep(NE_ACTIVE_STEP.BRANCH_SELECTION);
        return;
      }

      setErrorMessage(getCiamEmailErrorContent(result.errorType));
    }
  };
  const hasErrors = useMemo(
    () => !formik.isValid || errorMessage !== '',
    [formik.values.email, formik.errors.email, errorMessage, formik.isValid]
  );

  useEffect(() => {
    setErrorMessage('');
  }, [formik.values.email]);
  const onBack = () => {
    window.history.back();
  };

  const onSubmit = () => {
    checkEmail();
  };
  if (activeStep !== NE_ACTIVE_STEP.EMAIL_INPUT) return null;

  return (
    <div className="ne-step-email-input">
      <div className="ne-step-email-input__sub-text">
        Bitte geben Sie hier Ihre gültige E-Mail-Adresse an, die von uns als Kontakt zu Ihnen
        genutzt wird. Nach einer erfolgreichen Eintragung ins Installateurverzeichnis nutzen Sie
        Ihre E-Mail-Adresse gleichzeitig für die Zugänge zu unseren Systemen.
        {/* {isSubAccountEnabled()
          ? ' Sie können für jede Fachkraft einen eigenen Zugang anlegen.'
          : ' Für Ihre Firma erhalten Sie nur einen Zugang.'} */}
      </div>
      <div className="ne-step-email-input__email">
        <InputField
          fullWidth
          infoText=""
          key="email"
          label="E-Mail-Adresse *"
          name="email"
          oldValue=""
          variant="standard"
          value={formik.values.email}
          onChange={formik.handleChange}
          disabled={isLoading}
          error={Boolean(formik.errors.email)}
          helperText={formik.errors.email || ''}
        />
      </div>
      <FormSection>
        <div>
          Mit der Eintragung ins Installateurverzeichnis erhalten Sie Zugang zu den
          Netzanschlussportalen aller Partnerunternehmen:
        </div>
        <div>
          <a
            href="https://www.avacon-netz.de/de/meinauftragsportal.html"
            target="_blank"
            rel="noreferrer"
            className="link">
            Avacon Netz GmbH
          </a>
          ,{' '}
          <a
            href="https://www.bayernwerk-netz.de/de/meinauftragsportal.html"
            target="_blank"
            className="link"
            rel="noreferrer">
            Bayernwerk Netz GmbH
          </a>
          ,{' '}
          <a
            href="https://www.e-dis-netz.de/de/meinauftragsportal.html"
            target="_blank"
            rel="noreferrer"
            className="link">
            E.DIS Netz GmbH
          </a>
          ,{' '}
          <a
            href="https://www.elbenergie.com/de/meinauftragsportal.html"
            target="_blank"
            rel="noreferrer"
            className="link">
            ElbEnergie GmbH
          </a>
          ,{' '}
          <a
            href="https://www.hansegas.com/de/meinauftragsportal.html"
            target="_blank"
            rel="noreferrer"
            className="link">
            HanseGas GmbH
          </a>
          ,{' '}
          <a
            href="https://portal.lew-verteilnetz.de/de/meinauftragsportal.html"
            target="_blank"
            rel="noreferrer"
            className="link">
            LEW Verteilnetz GmbH
          </a>
          ,{' '}
          <a
            href="https://www.sh-netz.com/de/meinauftragsportal.html"
            target="_blank"
            rel="noreferrer"
            className="link">
            NordNetz GmbH
          </a>
          ,{' '}
          <a
            href="https://www.sh-netz.com/de/meinauftragsportal.html"
            target="_blank"
            rel="noreferrer"
            className="link">
            Schleswig-Holstein Netz GmbH
          </a>
          ,{' '}
          <a
            href="https://www.mitnetz-strom.de/marktpartner/installateure-elektrofachkraft/meinauftragsportal"
            target="_blank"
            rel="noreferrer"
            className="link">
            Mitteldeutsche Netzgesellschaft Strom mbH
          </a>
          ,{' '}
          <a
            href="https://www.mitnetz-gas.de/marktpartner/installateure/meinauftragsportal"
            target="_blank"
            rel="noreferrer"
            className="link">
            Mitteldeutsche Netzgesellschaft Gas mbH
          </a>
          {' und '}
          <a
            href="https://www.plauen-netz.de/marktpartner/installateure-elektrofachkraft/meinauftragsportal"
            target="_blank"
            rel="noreferrer"
            className="link">
            Verteilnetz Plauen GmbH
          </a>
        </div>
        <ErrorBanner>{errorMessage}</ErrorBanner>
      </FormSection>
      <div className="ne-form__action-buttons">
        <FormActionButtons
          loading={isLoading}
          hasErrors={hasErrors}
          titleLeft="Zurück"
          titleRight={activeStep === NE_ACTIVE_STEP.FORM_UPLOAD ? 'Antrag einreichen' : 'Weiter'}
          onClickLeft={onBack}
          onClickRight={onSubmit}
          errorMessage=""
        />
      </div>
      <FFormDebug values={formik.values} errors={formik.errors} />
    </div>
  );
};
